/**
 * The checklist on a task: the steps somebody meant to take, ticked off one at
 * a time while the task itself stays open.
 *
 * ── TICKING THE LAST BOX DOES NOT COMPLETE THE TASK ──────────────────────────
 *
 * Nothing in this file writes `status`, `completedAt` or `progress`, nothing
 * calls `updateTask`, and the card must never grow a control that implies it
 * will. The API refuses the same thing on purpose and writes out why at length
 * (`crm-projects/checklist.routes.ts`): a checklist is one person's plan for how
 * they intend to get the work done, written before the work starts and routinely
 * half of it, so the last tick moving the card would tell the client (the portal
 * reads `visibleToClient` tasks) and stamp a completion date off a list somebody
 * jotted down in a hurry. The head therefore counts steps and says nothing about
 * the task, and the count deliberately reads "5 of 5 steps done" rather than
 * anything that could be mistaken for a verdict on the task.
 *
 * ── A TICK THAT FAILED HAS TO LOOK LIKE ONE ──────────────────────────────────
 *
 * Every write here is optimistic, because a box that waits for a round trip
 * before it fills is a box the operator clicks twice. The other half of that
 * bargain is the one that matters: a failed write puts the row back EXACTLY as
 * it was and says so. A box left ticked over a write that did not land is the
 * worst outcome on this card - the operator has already moved on, and nobody
 * checks a tick twice.
 *
 * ── ORDER ────────────────────────────────────────────────────────────────────
 *
 * Up and down buttons, not drag. A drag is a whole interaction with failure
 * modes of its own, and this repo has paid for two of them already (a
 * `setPointerCapture` that swallowed every click on the infinite canvas, and a
 * pointer drag that could not be driven from a keyboard at all). Two buttons are
 * honest about what they do, work from the keyboard for free because they are
 * buttons, and are what `Milestones` next door already uses for the same job -
 * the reorder call and the optimistic-then-reconcile shape below are lifted from
 * it deliberately, so the two lists behave the same way under a failure.
 *
 * The list is kept locally and only SEEDED from the prop, the way `Milestones`
 * keeps its rows: the task page reloads the whole task after a comment is
 * posted, and a fresh array dropped over a half-finished reordering would take
 * the operator's work away mid-move.
 */
import { useEffect, useState } from "react";
import { CheckSquare, ChevronDown, ChevronUp, Loader2, Plus, Trash2, UserPlus } from "lucide-react";
import { Checkbox, cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import {
  addChecklistItem, deleteChecklistItem, reorderChecklist, updateChecklistItem,
  type ChecklistItem,
} from "../../../lib/projects";
import { Btn, Card, CardHead, useToast } from "../../ui";
import { UserAvatars } from "../_shared/UserChips";
import { UserPicker } from "../_shared/UserPicker";

/**
 * A card body's padding.
 *
 * `crm-card` is a surface and nothing else - the radius and the shadow, no
 * padding - so anything that is not a `crm-card-head` or a `crm-rows` has to say
 * where its edges are. The same constant the two cards either side of this one
 * declare for themselves; see the note in `TaskFacts.tsx` for why the repeat is
 * the house pattern rather than an oversight.
 */
const BODY = "px-6 pb-5 pt-3";

/** The route's ceiling on one step's text (`TEXT_MAX` in `checklist.routes.ts`).
 *  Enforced on the field as well as on the wire, because the endpoint answers a
 *  long one with a bare `invalid_body` that names no field - and a form that
 *  cannot say what went wrong is a form the operator retypes. */
const TEXT_MAX = 500;

export function TaskChecklist({
  taskId,
  items: seed,
  className,
}: {
  taskId: string;
  /** The boxes as the task detail read them, in the server's order. Seeded into
   *  local state below rather than rendered directly - see the header. */
  items: ChecklistItem[];
  /** Passed through to the card. The task page uses it to place this card in the
   *  single-column order it wants on a phone (`crm-w3`). */
  className?: string;
}) {
  const t = useT();
  const toast = useToast();
  const [items, setItems] = useState<ChecklistItem[]>(seed);
  const [text, setText] = useState("");
  /** Who the NEXT step is for. Reset after every add: handing four steps in a
   *  row to the same person is rarer than adding four steps for nobody, and a
   *  sticky owner is the kind of state that assigns work by accident. */
  const [owner, setOwner] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [ordering, setOrdering] = useState(false);
  /** The one row whose owner is being changed, if any. One at a time: a picker
   *  open on every row would be six search panels stacked inside a card. */
  const [assigning, setAssigning] = useState<string | null>(null);

  useEffect(() => {
    setItems(seed);
  }, [seed]);

  const done = items.filter((x) => x.done).length;

  async function add(e: React.FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body || adding) return;
    setAdding(true);
    try {
      const item = await addChecklistItem(taskId, { text: body, assignee: owner });
      // Appended rather than re-fetched: the server puts a new item one past the
      // highest stored order, so the end of the list is where it belongs and the
      // array here already says so.
      setItems((prev) => [...prev, item]);
      setText("");
      setOwner(null);
    } catch {
      toast(t("common.error"));
    } finally {
      setAdding(false);
    }
  }

  /**
   * Tick or untick one box.
   *
   * Optimistic, and the rollback is the point of the function rather than a
   * courtesy at the end of it: the row goes back to the value it HELD, not to
   * the opposite of what was just drawn, so two quick clicks on a failing write
   * cannot leave the box in a third state nobody chose.
   */
  async function toggle(item: ChecklistItem) {
    const next = !item.done;
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, done: next } : x)));
    try {
      await updateChecklistItem(item.id, { done: next });
    } catch {
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, done: item.done } : x)));
      toast(t("common.error"));
    }
  }

  /** Hand one step to somebody, or take it back off them. Same bargain as the
   *  tick above: drawn at once, put back if the write is refused. */
  async function assign(item: ChecklistItem, assignee: string | null) {
    setAssigning(null);
    if (assignee === item.assignee) return;
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, assignee } : x)));
    try {
      await updateChecklistItem(item.id, { assignee });
    } catch {
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, assignee: item.assignee } : x)));
      toast(t("common.error"));
    }
  }

  async function remove(item: ChecklistItem) {
    const before = items;
    setItems((prev) => prev.filter((x) => x.id !== item.id));
    try {
      await deleteChecklistItem(item.id);
    } catch {
      setItems(before);
      toast(t("common.error"));
    }
  }

  /**
   * Move one step one place.
   *
   * The whole id list goes in one call and the server renumbers it in one
   * transaction, so the order is never half written - and the rows it answers
   * with are what gets drawn, not the guess made here. That difference is real:
   * a step added by somebody else in another tab is not in the list this
   * component sent, and the server keeps it where it is rather than folding it
   * into the caller's numbering.
   */
  async function move(index: number, dir: -1 | 1) {
    const to = index + dir;
    if (ordering || to < 0 || to >= items.length) return;

    const before = items;
    const next = before.slice();
    const [row] = next.splice(index, 1);
    if (!row) return;
    next.splice(to, 0, row);

    setItems(next);
    setOrdering(true);
    try {
      const res = await reorderChecklist(taskId, next.map((x) => x.id));
      setItems(res.rows);
    } catch {
      setItems(before);
      toast(t("common.error"));
    } finally {
      setOrdering(false);
    }
  }

  return (
    <Card className={className}>
      <CardHead
        title={
          <span className="flex items-center gap-1.5">
            <CheckSquare className="size-3.5" />
            {t("tasks.checklist")}
          </span>
        }
        /* The count, and only where there is something to count. "0 of 0 steps
           done" on a task nobody has broken up yet is a figure about nothing,
           and the empty state below already says what to do about it. */
        actions={
          items.length > 0 ? (
            <span className="crm-mut crm-tnum text-[12.5px]">
              {t("tasks.checklistProgress", { done, n: items.length })}
            </span>
          ) : null
        }
      />

      <div className={BODY}>
        {items.length === 0 ? (
          <p className="crm-mut text-[13px]">{t("tasks.checklistEmpty")}</p>
        ) : (
          <ul className="flex flex-col">
            {items.map((item, i) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-2 border-t border-[var(--crm-hair)] py-2 text-[13px] first:border-t-0 first:pt-0"
              >
                {/* The box and its text are one label, so the words are the
                    control's accessible name and clicking them ticks it. The
                    buttons stay OUTSIDE the label: a button inside a label
                    toggles the box on its way to doing its own job. */}
                <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5">
                  {/* The shared Radix box, the one the permissions card and the
                      call queue use. The browser's own square was the last
                      control on this page still drawn by the operating system. */}
                  <Checkbox
                    className="mt-0.5"
                    checked={item.done}
                    onCheckedChange={() => void toggle(item)}
                    /* Names what the box marks: this STEP, not the task. The
                       task is finished by the status control in the page head
                       and by nothing on this card. */
                    title={t("tasks.checklistDone")}
                  />
                  <span className={cn("min-w-0", item.done && "crm-mut line-through")}>{item.text}</span>
                </label>

                {/* Drawn only where somebody is on it. `UserAvatars` has a mark
                    for an empty list, which is right in a table cell that has to
                    hold a track open and wrong here: most steps belong to
                    whoever is working the task, and a "nobody" mark on every row
                    would be a badge that means nothing. */}
                {item.assignee && assigning !== item.id ? (
                  <UserAvatars ids={[item.assignee]} size={22} />
                ) : null}

                <span className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    className={cn("crm-cb crm-cb-sm", assigning === item.id && "is-on")}
                    onClick={() => setAssigning((prev) => (prev === item.id ? null : item.id))}
                    title={t("projects.assignee")}
                    aria-label={t("projects.assignee")}
                  >
                    <UserPlus className="size-4" />
                  </button>
                  {/* `projects.milestoneUp` / `Down` read "Move up" / "Move down"
                      in both catalogues - generic sentences under a key named
                      after the first list that needed them. Borrowed rather than
                      duplicated: two keys with identical text are two things to
                      keep in step, and adding a `common.moveUp` is a catalogue
                      change, which this ring deliberately does not make. */}
                  <button
                    type="button"
                    className="crm-cb crm-cb-sm"
                    onClick={() => void move(i, -1)}
                    disabled={ordering || i === 0}
                    title={t("projects.milestoneUp")}
                    aria-label={t("projects.milestoneUp")}
                  >
                    <ChevronUp className="size-4" />
                  </button>
                  <button
                    type="button"
                    className="crm-cb crm-cb-sm"
                    onClick={() => void move(i, 1)}
                    disabled={ordering || i === items.length - 1}
                    title={t("projects.milestoneDown")}
                    aria-label={t("projects.milestoneDown")}
                  >
                    <ChevronDown className="size-4" />
                  </button>
                  <button
                    type="button"
                    className="crm-cb crm-cb-sm hover:text-red-600"
                    onClick={() => void remove(item)}
                    title={t("common.delete")}
                    aria-label={t("common.delete")}
                  >
                    <Trash2 className="size-4" />
                  </button>
                </span>

                {/* Opened per row rather than drawn on every one: the picker is
                    a search field with a panel under it, and six of them inside
                    one card is a card nobody can read. It takes the whole width
                    below the step so the text above it does not have to shrink
                    to a column of two words. */}
                {assigning === item.id ? (
                  <span className="w-full">
                    <UserPicker
                      value={item.assignee}
                      onChange={(next) => void assign(item, next)}
                      placeholder={t("projects.assignee")}
                    />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-3 flex flex-col gap-2">
          {/* Field and button on one wrapping row, the shape `Milestones` uses
              for its own quick-add. The button says what it does in words rather
              than being a bare `+`: this is the control that turns an empty card
              into a plan, so it is the one thing here that should not need
              working out. */}
          <form onSubmit={add} className="flex flex-wrap items-center gap-2">
            <label className="crm-inp min-w-52 flex-1">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t("tasks.checklistPlaceholder")}
                maxLength={TEXT_MAX}
                autoComplete="off"
              />
            </label>
            <Btn type="submit" tone="dark" disabled={adding || !text.trim()}>
              {adding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t("tasks.checklistAdd")}
            </Btn>
          </form>

          {/* Who the step is for, offered on the way in - a step handed over at
              the moment it is written is the common case, and the row control
              above is for changing it afterwards.

              OUTSIDE the form on purpose, and this is not a layout preference:
              `UserPicker`'s box is a text input, and an Enter pressed while
              searching for a colleague would reach the form and add the step
              half-typed. Its own state lives in this component, so the submit
              above still picks the owner up. */}
          <span className="block">
            <span className="crm-lab">{t("projects.assignee")}</span>
            <UserPicker value={owner} onChange={setOwner} placeholder={t("projects.assignee")} />
          </span>
        </div>
      </div>
    </Card>
  );
}
